import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowRight, ArrowLeft, UserPlus, Lock } from "lucide-react";
import { APP_NAME } from "@/lib/branding";
import { BrandLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

export default function AcceptInvite() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invitations/${token}`);
        setInv(data);
        setFullName(data.full_name || "");
      } catch (e) {
        setError(apiError(e));
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/invitations/accept", { token, password, full_name: fullName });
      await refresh();
      navigate("/app");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6 antialiased relative overflow-hidden"
      style={{ fontFamily: "'Plus Jakarta Sans', 'Manrope', system-ui, sans-serif" }}
      data-testid="accept-invite-page"
    >
      {/* Decorative grid background */}
      <div
        className="absolute inset-0 opacity-[0.35] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#E2E8F0 1px, transparent 1px), linear-gradient(90deg, #E2E8F0 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse 60% 60% at 50% 40%, #000 40%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 60% 60% at 50% 40%, #000 40%, transparent 100%)",
        }}
      />
      <div className="absolute -top-32 right-1/4 h-96 w-96 rounded-full bg-indigo-100/60 blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative">
        {/* Brand */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/welcome" className="flex items-center gap-2.5 group">
            <BrandLogo variant="icon" className="h-8 w-8" />
            <span className="font-bold tracking-tight text-[15px] text-[#0B0F17]">
              {APP_NAME}
            </span>
          </Link>
          <Link
            to="/welcome"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#0B0F17] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Retour au site
          </Link>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full ring-1 ring-indigo-200 bg-indigo-50/80 mb-5">
            <UserPlus className="h-3 w-3 text-[#4F46E5]" />
            <span className="text-[11px] font-semibold text-[#4338CA] tracking-wide">
              Invitation reçue
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0B0F17] leading-[1.15]">
            Rejoignez votre équipe
          </h1>
          {inv ? (
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Vous êtes invité·e à rejoindre{" "}
              <span className="font-semibold text-[#0B0F17]">{inv.tenant_name}</span>{" "}
              en tant que{" "}
              <span className="font-semibold text-[#0B0F17]">
                {t(`roles.${inv.role}`)}
              </span>
              . Créez votre mot de passe pour activer votre accès.
            </p>
          ) : (
            <p className="mt-2 text-sm text-slate-400">Chargement de l'invitation…</p>
          )}

          {error && (
            <Alert variant="destructive" className="mt-5 border-red-200 bg-red-50" data-testid="invite-accept-error">
              <AlertDescription className="text-red-900 text-sm">{error}</AlertDescription>
            </Alert>
          )}

          {inv && (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="invite-accept-form">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                  {t("common.email")}
                </Label>
                <Input
                  value={inv.email}
                  disabled
                  className="h-11 bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                  {t("auth.fullName")}
                </Label>
                <Input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  placeholder="Prénom Nom"
                  data-testid="accept-name-input"
                  className="h-11 border-slate-200 focus:border-[#4F46E5] focus:ring-[#4F46E5]/20 placeholder:text-slate-400"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                  {t("common.password")}
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="••••••••"
                  data-testid="accept-password-input"
                  className="h-11 border-slate-200 focus:border-[#4F46E5] focus:ring-[#4F46E5]/20 placeholder:text-slate-300"
                />
                <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                  <Lock className="h-3 w-3" />
                  Au moins 8 caractères.
                </p>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="accept-submit-btn"
                className={cn(
                  "group w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-semibold transition-all",
                  "bg-[#0B0F17] text-white hover:bg-[#1E293B] shadow-lg shadow-slate-900/10 hover:shadow-slate-900/20",
                  "disabled:opacity-60 disabled:cursor-not-allowed",
                )}
              >
                {loading ? t("common.loading") : "Rejoindre l'équipe"}
                {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />}
              </button>
            </form>
          )}
        </div>

        <div className="mt-6 text-center text-[11px] text-slate-400">
          Déjà un compte ?{" "}
          <Link to="/login" className="font-semibold text-[#4F46E5] hover:text-[#4338CA]">
            Se connecter
          </Link>
        </div>
      </div>
    </div>
  );
}
